import { ReferenceStock } from '@1po/1po-bff-fe-spec/generated/catalog/trading_data/model/ReferenceStock';
import moment from 'moment/moment';
import queryString from 'query-string';
import { ReferencePriceType } from 'domains/references';
import { ClientView, SparePartsViewType } from 'domains/user';
import { env } from 'env';
import { FOUND, NO_DATA, SearchData } from 'utils/dataStatus';
import { Language } from 'utils/i18n/Language';

export function head<T>(values: T[] | undefined): T | undefined {
  return values && values.length > 0 ? values[0] : undefined;
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function mergeObjectInMap<K, V, O extends V>(map: Map<K, V>, key: K, object: O): void {
  const val = map.get(key);
  const newObject = val ? mergeDeep(val, object) : getNotEmpty(object);
  map.set(key, newObject);
}

function sortPricesByRecommendedPrice(
  pricesMap: Map<string, ReferencePriceType>,
  catalogView: SparePartsViewType = ClientView,
): Map<string, ReferencePriceType> {
  return new Map(
    [...pricesMap].sort(([, a], [, b]) => {
      const priceA =
        catalogView === ClientView
          ? Number(a.clientView?.recommendedPriceVatExcluded ?? 0)
          : Number(a.garageView?.vatExcludedPrice ?? 0);

      const priceB =
        catalogView === ClientView
          ? Number(b.clientView?.recommendedPriceVatExcluded ?? 0)
          : Number(b.garageView?.vatExcludedPrice ?? 0);

      return priceA - priceB;
    }),
  );
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function mergePricesInMap<K>(
  map: Map<K, Map<string, ReferencePriceType> | NO_DATA>,
  key: K,
  value: Map<string, ReferencePriceType> | NO_DATA,
  catalogView: SparePartsViewType = ClientView,
): void {
  const existingValue = map.get(key);

  if (!existingValue || !(existingValue instanceof Map)) {
    map.set(key, value);
    return;
  }

  if (existingValue instanceof Map && value instanceof Map) {
    const mergedMap = new Map(existingValue);

    for (const [innerKey, innerValue] of value.entries()) {
      mergeObjectInMap(mergedMap, innerKey, innerValue);
    }

    const sortedMap = sortPricesByRecommendedPrice(mergedMap, catalogView);
    map.set(key, sortedMap);
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function mergeStocksInSearchDataMap<K>(
  map: Map<K, SearchData<Map<string, ReferenceStock>>>,
  key: K,
  value: SearchData<Map<string, ReferenceStock>>,
): void {
  const existingValue = map.get(key);

  if (!existingValue?.data) {
    map.set(key, value);
    return;
  }

  // both maps, merge it
  if (existingValue.data instanceof Map && value.data instanceof Map) {
    const mergedMap = new Map(existingValue.data);

    for (const [innerKey, innerValue] of value.data.entries()) {
      mergeObjectInMap(mergedMap, innerKey, innerValue);
    }

    map.set(key, { searchStatus: FOUND, data: mergedMap });
  }
}

const isObject = (obj: any): boolean => obj && typeof obj === 'object';

function getNotEmpty(source: any) {
  if (isObject(source)) {
    const isEmpty = getArrayWithoutUndefinedMembers(Object.values(source));
    return isEmpty.length === 0 ? undefined : source;
  }
  return source;
}

/**
 * @author inspired by [jhildenbiddle](https://stackoverflow.com/a/48218209).
 */
function mergeDeep(target: any, source: any) {
  if (!isObject(target) || !isObject(source)) {
    return getNotEmpty(source);
  }

  Object.keys(source).forEach((key) => {
    const targetValue = target[key];
    const sourceValue = source[key];

    if (Array.isArray(targetValue) && Array.isArray(sourceValue)) {
      target[key] = sourceValue;
    } else if (isObject(targetValue) && isObject(sourceValue)) {
      target[key] = mergeDeep(Object.assign({}, targetValue), sourceValue);
    } else {
      target[key] = sourceValue;
    }
  });

  return target;
}

export function compareArrays<T>(a: T[], b: T[]): boolean {
  return a.length === b.length && a.every((element, index) => element === b[index]);
}

/**
 * Sorts array of strings based on Numeric values
 * @param array
 */
export function localeCompareSort(array: string[]): string[] {
  return array.sort(function (a, b) {
    return a.localeCompare(b, undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  });
}

export const getArrayWithoutUndefinedMembers = <T>(arr: Array<T | undefined>): Array<T> => arr.filter((x) => x) as T[];

export function decodeBase64(base64: string): string {
  try {
    return atob(base64);
  } catch (e) {
    return '';
  }
}

export const stringifyUrlQueryParams = (obj: unknown): string => {
  try {
    return queryString.stringify(obj as Record<string, unknown>, {
      arrayFormat: 'comma',
      skipEmptyString: true,
      skipNull: true,
    });
  } catch (e) {
    return '';
  }
};

export const toStringArray = (value: unknown): string[] => {
  if (value === null || value === undefined || !value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.filter((item) => item !== null && item !== undefined && item).map((item) => String(item));
  }

  return [String(value)];
};

export const isNonEmptyString = (value: unknown): boolean => {
  return typeof value === 'string' && value.trim().length > 0;
};

export const validateLocaleImports = (): void => {
  const availableMomentLocales = moment.locales().map((l) => l.toLowerCase());

  const getPrimaryTag = (locale: string): string => locale.toLowerCase().split('-')[0];

  const isLocaleAvailable = (langCode: string): boolean => {
    const primary = getPrimaryTag(langCode);
    return availableMomentLocales.includes(langCode.toLowerCase()) || availableMomentLocales.includes(primary);
  };

  const missingLocales = Object.values(Language).filter((lang) => !isLocaleAvailable(lang.locale));
  if (missingLocales.length > 0) {
    const warningMessage = `Moment locales imports for ${missingLocales.join(', ')} are missing.`;
    if (env.REACT_APP_ENVIRONMENT === 'local') {
      throw new Error(`${warningMessage} Add them to the App.tsx file.`);
    } else {
      console.warn(warningMessage);
    }
  }
};
